import './TutorCard.scss';

import PropTypes from 'prop-types';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import useProfileLike from 'shared/hooks/useProfileLike';
import useUser from 'shared/hooks/useUser';
import { selectUniversitiesEntities } from 'shared/store/schools';
import { degreeTypeMap } from 'shared/utils/formOptions';

import useSignUpModal from 'components/modals/SignUpModal';
import Avatar from 'components/ui/Avatar';
import Card from 'components/ui/Card';
import Like from 'components/ui/Like';
import StarRating from 'components/ui/StarRating';
import SubjectsList from 'components/ui/SubjectsList';
import useChat from 'hooks/useChat';
import { ReactComponent as AwardIcon } from 'assets/icons/award.svg';
import { ReactComponent as BookIcon } from 'assets/icons/book-reader.svg';
import { ReactComponent as GraduationIcon } from 'assets/icons/graduation.svg';
import { ReactComponent as PartnerIcon } from 'assets/icons/partner.svg';

const TutorCard = ({ tutor, ...rest }) => {
  const {
    id,
    isMonthTutor,
    nickname,
    picture,
    rating,
    schoolList,
    subjects,
    hasPartner,
  } = tutor;
  const { t } = useTranslation('common');
  const universitiesEntities = useSelector(selectUniversitiesEntities);
  const { likeMutation, isLiked } = useProfileLike({
    id,
    userType: 'tutor',
    initialIsLike: tutor.isLiked,
  });
  const signUpModal = useSignUpModal();
  const { thisUser, isStudent } = useUser();
  const { createChannel } = useChat();

  const getRating = () => {
    if (!rating) return 0;
    const { classPreparationRating, lectureRating, punctualityRating } = rating;
    return (classPreparationRating + lectureRating + punctualityRating) / 3;
  };

  const getRepresentativeSchool = () => {
    const representative =
      schoolList?.find(({ isRepresentative }) => isRepresentative) ||
      schoolList?.[0];

    if (representative) {
      return {
        universityName: universitiesEntities[representative.schoolId]?.name,
        degreeType: degreeTypeMap(t)[representative?.degreeType],
      };
    }
    return null;
  };

  const toggleLike = () => {
    if (thisUser) {
      if (isStudent) {
        likeMutation.mutate(undefined, {
          onSuccess: () => {
            createChannel(id);
          },
        });
      }
    } else {
      signUpModal({ redirect: `/tutors/${id}` });
    }
  };

  return (
    <Card
      className="tutor-card"
      to={`/tutors/${id}`}
      toggleLike={toggleLike}
      {...rest}
    >
      <div className="tutor-card-left">
        <Avatar size={78} src={picture} />
        <StarRating rating={getRating()} color="secondary" />
      </div>
      <div className="tutor-card-right">
        <div className="tutor-card-header">
          {hasPartner && <PartnerIcon />}
          {!!isMonthTutor && !hasPartner && <AwardIcon />}
          <h3>{nickname}</h3>
          <Like checked={isLiked} />
        </div>
        <dl>
          <dt>
            <GraduationIcon />
          </dt>
          <dd>
            {getRepresentativeSchool() ? (
              <>
                <p>{getRepresentativeSchool()?.degreeType}</p>
                <p>{getRepresentativeSchool()?.universityName}</p>
              </>
            ) : (
              '-'
            )}
          </dd>
          <dt>
            <BookIcon />
          </dt>
          <dd>
            <SubjectsList subjects={subjects} slice={2} hideOverflow />
          </dd>
        </dl>
      </div>
    </Card>
  );
};

TutorCard.propTypes = {
  tutor: PropTypes.object.isRequired,
};

export default memo(TutorCard);
